import React, { useCallback, useEffect, useMemo } from 'react';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { MenuItemProps } from '@televet/kibble-ui/build/components/Menu';
import { Select } from '@televet/kibble-ui/build/components/Select';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { useGetAppointmentModalAppointmentProvidersLazyQuery } from 'shared/types/graphql';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { updateAppointmentDraft, updateAppointmentModalErrors } from 'pages/Appointments/state/appointmentsModalSlice';
import useClinicUser from 'shared/hooks/useClinicUser';
import * as Sentry from '@sentry/react';
import { LinkedAutomationUpdates } from '..';

const DEFAULT_OPTION: MenuItemProps = { label: 'None', value: 'None' };

interface AppointmentProviderProps {
  updateLinkedAutomations: (updates: LinkedAutomationUpdates) => Promise<void>;
}

export const AppointmentProvider = ({ updateLinkedAutomations }: AppointmentProviderProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const { appointmentDraft, appointmentId, errors } = useAppSelector((state) => state.appointmentsModal);
  const dispatch = useAppDispatch();
  const toast = useToast();

  const [getAppointmentProviders, { data: appointmentProvidersData, loading: isLoadingAppointmentProviders }] =
    useGetAppointmentModalAppointmentProvidersLazyQuery({
      variables: { clinicId: currentClinicId || '' },
      onError: (e) => {
        toast({
          title: 'Sorry!',
          description: 'We are having trouble finding your appointment providers',
          status: 'error',
        });
        Sentry.captureException(e);
      },
    });

  const clinicEmployeesOptions: MenuItemProps[] = useMemo(() => {
    if (appointmentProvidersData?.clinicEmployees) {
      return [DEFAULT_OPTION].concat(
        appointmentProvidersData.clinicEmployees.map((employee) => {
          return {
            label: `${employee.firstName} ${employee.lastName}`,
            value: employee.id,
            isSelected: employee.id === appointmentDraft?.clinicEmployeeId,
          };
        }),
      );
    }
    return [];
  }, [appointmentDraft?.clinicEmployeeId, appointmentProvidersData?.clinicEmployees]);

  const handleUpdateProvider = useCallback(
    (selectedItem) => {
      dispatch(
        updateAppointmentDraft({
          clinicEmployeeId: selectedItem.value === 'None' ? null : selectedItem.value,
        }),
      );
      dispatch(updateAppointmentModalErrors(errors.filter((error) => error !== 'missingProvider')));
      updateLinkedAutomations({ clinicEmployeeId: selectedItem.value });
    },
    [dispatch, errors, updateLinkedAutomations],
  );

  const handleOnMenuOpen = useCallback(async () => {
    if (appointmentId) return;
    await getAppointmentProviders();
  }, [appointmentId, getAppointmentProviders]);

  useEffect(() => {
    // If editing appointment, fetch appointment providers on load so we can render selected value
    if (appointmentId) {
      getAppointmentProviders();
    }
  }, [appointmentId, getAppointmentProviders]);

  return (
    <Select
      className="AppointmentModal__AppointmentProvider"
      data-testid="appointment-provider-select"
      listProps={{
        isSearchable: true,
        onSelect: handleUpdateProvider,
        maxH: '250px',
        isLoading: isLoadingAppointmentProviders,
      }}
      containerProps={{
        onOpen: handleOnMenuOpen,
      }}
      defaultLeftElement={<Icon color="text.default" size="md" name="person" />}
      value={appointmentDraft?.clinicEmployeeId}
      placeholder="Select a provider"
      label="Appointment Provider"
      labelStyle={{ fontWeight: 'bold' }}
      options={clinicEmployeesOptions}
      isInvalid={errors.includes('missingProvider')}
      errorText="Please select a provider"
    />
  );
};
